/**
 * BlobShadows — sombra "de contato" macia para CADA avatar (carro/pessoa).
 *
 * Em vez de projetar o avatar num shadow map (que, no mapa que cobre a cidade
 * inteira, daria poucos texels por avatar → sombra dura e que "trava" ao mover),
 * desenhamos uma MANCHA macia (quad no chão, textura com borda esfumada) que
 * SEGUE o avatar a cada frame. Resultado: suave, fluida e muito barata — um único
 * InstancedMesh (uma draw call) para todas as manchas.
 */
import * as THREE from 'three';
import { probes, P } from '../perfProbes.js';
import { TEX_TIER } from '../scene/qualityLevel.js';

const CAP = 2048;
const IN_N = 5; // entradas que definem a matriz da mancha: x, z, heading, width, length
const _m = new THREE.Matrix4();
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler();
const _scl = new THREE.Vector3();

/**
 * A mancha é UMA textura compartilhada por todos os avatares. 64 px foi
 * calibrado para 1080p; num telão 4K a mancha do carro chega a ~100 px de tela e
 * a textura entra em MAGNIFICAÇÃO — a borda esfumada, que é o ponto dela, vira
 * degrau. Em Ultra vai a 256 (TEX_TIER.blobPx): custo por quadro zero (continua
 * um único InstancedMesh) e ~260 KB de VRAM.
 */
function blobTexture() {
  const S = TEX_TIER.blobPx;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(S / 2, S / 2, S / 64, S / 2, S / 2, S / 2 - S / 64);
  // núcleo mais SÓLIDO (sombra presente) e só esfumaça na borda externa
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.58, 'rgba(255,255,255,0.94)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class BlobShadows {
  constructor(scene) {
    const geo = new THREE.PlaneGeometry(1, 1);
    geo.rotateX(-Math.PI / 2); // deita no chão (normal +Y); largura→X, comprimento→Z
    const mat = new THREE.MeshBasicMaterial({
      map: blobTexture(),
      color: 0x000000,
      transparent: true,
      opacity: 0.62, // mais escura/presente (antes 0.4 → quase imperceptível)
      depthWrite: false
    });
    this.mesh = new THREE.InstancedMesh(geo, mat, CAP);
    this.mesh.name = 'blobShadows';
    this.mesh.count = 0;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    scene.add(this.mesh);
    this.count = 0;
    this.slotRef = new Array(CAP).fill(null);
    // ÚLTIMAS ENTRADAS por slot [x, z, heading, width, length]. A matriz da mancha é
    // função PURA destas 5 — inclusive da direção do sol, que já chega dissolvida
    // nelas (o EntityManager desloca x/z no sentido do sol, orienta `heading` pelo sol
    // e alonga `length` com a altura dele). Então "só atualizar quando posição, tipo ou
    // sol mudarem" cai naturalmente daqui, sem um caminho separado para o sol.
    // Float64Array: comparar contra o Float32 lido de volta daria falso-negativo.
    this.lastIn = new Float64Array(CAP * IN_N);
    this.hasIn = new Uint8Array(CAP);
    // faixa de linhas sujas neste quadro (−1 = nenhuma)
    this._lo = -1;
    this._hi = -1;
  }

  /** Marca a linha `slot` como suja, ampliando a faixa a enviar. */
  _touch(slot) {
    if (this._lo < 0 || slot < this._lo) this._lo = slot;
    if (slot > this._hi) this._hi = slot;
  }

  /** Reserva um slot de instância para uma entidade. */
  acquire(ref) {
    if (this.count >= CAP) return -1; // capacidade cheia → não corrompe o buffer
    const slot = this.count++;
    this.slotRef[slot] = ref;
    this.mesh.count = this.count;
    this.hasIn[slot] = 0; // sem estado anterior → a 1ª escrita nunca é pulada
    return slot;
  }

  /** Libera o slot (swap-remove): move a última mancha para o buraco. */
  release(slot) {
    if (slot < 0) return; // acquire falhou (CAP cheio) → nada a liberar
    const last = this.count - 1;
    if (slot !== last && last >= 0) {
      const moved = this.slotRef[last];
      moved.blobSlot = slot;
      this.slotRef[slot] = moved;
      // A linha `slot` fica com a mancha de quem saiu, e `lastIn[slot]` com as
      // entradas dele — os dois CONSISTENTES entre si (foi ele quem escreveu). Então
      // pular a próxima escrita do avatar movido, se por acaso as entradas dele
      // baterem, ainda daria a matriz certa. Invalidamos mesmo assim para que a
      // correção de um slot nunca dependa desse raciocínio sobre o ocupante anterior:
      // ele deixaria de valer no dia em que alguém mover a matriz aqui (é o que o
      // Fleet.release faz, com copyWithin, e lá a invalidação é obrigatória).
      this.hasIn[slot] = 0;
    }
    this.slotRef[last] = null;
    this.hasIn[last] = 0;
    this.count = Math.max(0, last);
    this.mesh.count = this.count;
  }

  /** Posiciona/dimensiona a mancha (largura×comprimento) no chão, sob o avatar. */
  write(slot, x, z, heading, width, length) {
    if (slot < 0) return; // entidade sem slot → nada a escrever
    probes.begin(P.sombras);
    const L = this.lastIn, o = slot * IN_N;
    // nada mudou → a matriz sairia bit a bit igual. Comparação EXATA de propósito:
    // assim o pulo é demonstravelmente invisível, não "quase igual".
    if (this.hasIn[slot] === 1 &&
        L[o] === x && L[o + 1] === z && L[o + 2] === heading &&
        L[o + 3] === width && L[o + 4] === length) { probes.end(P.sombras); return; }
    L[o] = x; L[o + 1] = z; L[o + 2] = heading; L[o + 3] = width; L[o + 4] = length;
    this.hasIn[slot] = 1;
    _euler.set(0, heading, 0);
    _quat.setFromEuler(_euler);
    _pos.set(x, 0.045, z); // logo acima do chão (evita z-fighting)
    _scl.set(width, 1, length);
    _m.compose(_pos, _quat, _scl);
    this.mesh.setMatrixAt(slot, _m);
    this._touch(slot);
    probes.end(P.sombras);
  }

  /**
   * Sobe à GPU SÓ a faixa de linhas que mudou.
   *
   * Antes marcava `needsUpdate` incondicionalmente e sem faixa — e sem faixa o three
   * reenvia o ARRAY INTEIRO (WebGLAttributes.updateBuffer): 2.048 linhas, 128 KB por
   * quadro, mesmo com 500 manchas vivas e mesmo quando nenhuma tinha mudado.
   */
  flush() {
    if (this._lo < 0) return; // nenhuma mancha mudou → não toca no buffer
    probes.begin(P.sombras);
    const a = this.mesh.instanceMatrix;
    a.clearUpdateRanges();
    a.addUpdateRange(this._lo * 16, (this._hi - this._lo + 1) * 16);
    a.needsUpdate = true;
    this._lo = this._hi = -1;
    probes.end(P.sombras);
  }
}
